/**
 * CycleDesignerWizard — 4-step visual cycle setup (Catchball paths + track creation).
 * Replaces CycleSetupWizard when cycle_designer_v2 is enabled.
 *
 * Theme: inherit light/dark from <html> (same CSS vars as the rest of the product).
 * Do not put className="dark" on this tree — that pins Design Paths to the
 * navy diagram palette even when the user is in light mode.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { CompassSpinner } from '@/components/ui/CompassSpinner';
import { DesignerLeftRail } from './components/DesignerLeftRail';
import { useCycleDesignerState } from './hooks/useCycleDesignerState';
import { buildOrgPathNodes } from './lib/orgPathModel';
import { buildCustomPathNodes, customAnchorNode, pathModeOf } from './lib/customPathModel';
import { isCycleDesignerComplete } from './lib/cycleDesignerPhase';
import { pathsFromLaunchedTracks, readDesignerDraft } from './lib/designerDraft';
import { useCycleDesignerEnabled } from './hooks/useCycleDesignerEnabled';
import { useDesignerDraftAutosave } from './hooks/useDesignerDraftAutosave';
import {
  buildDesignerLaunchUrl,
  launchCycleDesigner,
} from './lib/launchCycleDesigner';
import { StepSetup } from './steps/StepSetup';
import { StepDesignPaths } from './steps/StepDesignPaths';
import { StepParameters } from './steps/StepParameters';
import { StepReviewLaunch } from './steps/StepReviewLaunch';
import type { DesignerStep } from './types';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useEnterpriseIdentityStore } from '@/stores/prioritization/enterpriseIdentityStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useCatchballLoopStore } from '@/stores/prioritization/catchballLoopStore';
import { useCatchballVirtualConfigStore } from '@/stores/prioritization/catchballVirtualConfigStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useUserCycleRoleContext } from '@/hooks/prioritization/useUserCycleRoleContext';
import { useUserStore } from '@/stores/prioritization/userStore';
import { canSync } from '@/lib/permissionResolver';
import { useWorkspaceModulesReady } from '@/lib/workspaceModulesService';
import type { CatchballLoop } from '@/types/prioritization/catchballLoop';
import type { Track } from '@/types/prioritization/track';
import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';

/** Stable empty array for Zustand selectors — never inline `[]` in getSnapshot. */
const EMPTY_CYCLE_LOOPS: CatchballLoop[] = [];
const EMPTY_TRACKS: Track[] = [];

/** Text on mustard/green fills: navy in dark, near-white in light. */
const INK_ON_ACCENT = 'var(--bg)';

const STEP_LABELS: Record<DesignerStep, string> = {
  1: 'Cycle Setup',
  2: 'Design Paths',
  3: 'Guidance',
  4: 'Review & Launch',
};

const STEP_HINTS: Record<DesignerStep, string> = {
  1: 'Step 1 of 4 — Name the cycle and set its dates.',
  2: 'Step 2 of 4 — Click org nodes to build ball paths. Each path defines one Track route.',
  3: 'Step 3 of 4 — Tell each Track what a good response should answer.',
  4: 'Step 4 of 4 — Review and launch the cycle.',
};

export function CycleDesignerWizard() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const modulesReady = useWorkspaceModulesReady();
  const designerEnabled = useCycleDesignerEnabled();

  const cycle = useDegapProcessStore((s) =>
    cycleId ? s.cycles.find((c) => c.id === cycleId) : undefined,
  );
  const fetchCycleById = useDegapProcessStore((s) => s.fetchCycleById);
  const updateCycle = useDegapProcessStore((s) => s.updateCycle);
  const orgUnitsMap = useEnterpriseIdentityStore((s) => s.orgUnits);
  const orgUnitsList = useMemo(() => Array.from(orgUnitsMap.values()), [orgUnitsMap]);
  const fetchEnterpriseIdentity = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);
  const createTrack = useTrackStore((s) => s.createTrack);
  const archiveTrack = useTrackStore((s) => s.archiveTrack);
  const fetchTracksForCycle = useTrackStore((s) => s.fetchTracksForCycle);
  const getTracksForCycle = useTrackStore((s) => s.getTracksForCycle);
  const loops = useCatchballLoopStore(
    useShallow((s) =>
      cycleId ? s.loops.filter((l) => l.cycleId === cycleId) : EMPTY_CYCLE_LOOPS,
    ),
  );
  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const createLoop = useCatchballLoopStore((s) => s.createLoop);
  const updateLoopIntent = useCatchballLoopStore((s) => s.updateLoopIntent);
  const updateTrack = useTrackStore((s) => s.updateTrack);

  const cycleRoleContext = useUserCycleRoleContext({ cycleId, activeLoopId: null });
  const canConfigure =
    cycleRoleContext.isCycleOwner ||
    cycleRoleContext.isAdmin ||
    (workspaceId ? canSync('process.edit', { kind: 'resource', id: workspaceId }) : false);

  const orgScopeNodeId = cycle?.orgScopeNodeId;
  const { nodes: orgNodes, anchor, maxRelativeLevel } = useMemo(
    () => buildOrgPathNodes(orgUnitsList, workspaceId, orgScopeNodeId),
    [orgUnitsList, workspaceId, orgScopeNodeId],
  );
  const anchorId = anchor?.id ?? orgScopeNodeId ?? '';

  const designer = useCycleDesignerState(cycle, anchorId || undefined);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // Custom paths name their own stops, so the node list every downstream
  // consumer reads (level config, cadence, launch, review) is derived from the
  // design itself rather than from `org_units`. Feeding them through the same
  // OrgPathNode shape is what lets those consumers stay mode-agnostic.
  const pathMode = pathModeOf(designer.form);
  const orgChartAvailable = Boolean(anchorId) && orgNodes.length > 0;
  // Level 0 belongs to the cycle's owner: the anchor names them, an unowned
  // unit falls back to them, and a cycle without one says so at L0. (This was
  // the signed-in person's name, whoever they were.)
  const cycleOwnerId = cycle?.ownerId?.trim() || null;
  const ownerNames = useActorDisplayMap(useMemo(() => (cycleOwnerId ? [cycleOwnerId] : []), [cycleOwnerId]));
  const cycleOwnerName = cycleOwnerId ? ownerNames[cycleOwnerId] ?? null : null;
  const cycleOwnerRef = useMemo<CatchballParticipantRef | null>(
    () => (cycleOwnerId ? { type: 'user', id: cycleOwnerId, displayName: cycleOwnerName ?? 'Cycle owner' } : null),
    [cycleOwnerId, cycleOwnerName],
  );
  const customAnchorLabel = cycleOwnerName || 'Cycle owner';

  const effectiveOrgNodes = useMemo(
    () =>
      pathMode === 'custom'
        ? [customAnchorNode(customAnchorLabel), ...buildCustomPathNodes(designer.paths)]
        : orgNodes,
    [pathMode, customAnchorLabel, designer.paths, orgNodes],
  );
  const effectiveAnchor = pathMode === 'custom' ? effectiveOrgNodes[0] ?? null : anchor;
  const effectiveMaxRelativeLevel =
    pathMode === 'custom'
      ? effectiveOrgNodes.reduce((max, n) => Math.max(max, n.relativeLevel), 0)
      : maxRelativeLevel;
  const effectiveAnchorId = designer.effectiveAnchorId;

  // The wizard must end exactly at the viewport bottom or the footer (and its
  // next-step button) falls below the fold. The chrome above it is not a fixed
  // 64px — CycleContextBar and banners come and go — so measure instead of guess.
  const rootRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const fit = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      el.style.height = `${window.innerHeight - top}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    const observer = new ResizeObserver(fit);
    observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', fit);
      observer.disconnect();
    };
  });

  // Autosave the in-progress design into cycle.metadata.designerDraft (see
  // useDesignerDraftAutosave for the flush-on-cleanup rationale).
  // The Launch button's own inputs, so a complete design is also saved as its
  // launch_cycle_design payload (metadata.launchPayload) for iOS to launch.
  const launchOrgById = useMemo(
    () => designer.buildOrgById(effectiveOrgNodes),
    [designer, effectiveOrgNodes],
  );
  useDesignerDraftAutosave({
    cycleId,
    canConfigure,
    launching,
    form: designer.form,
    paths: designer.paths,
    step: designer.step,
    launch: {
      orgNodes: effectiveOrgNodes,
      orgById: launchOrgById,
      pathMode,
      ownerRef: cycleOwnerRef ?? undefined,
    },
  });

  // Reopen (?reopen=1) on a launched design: the draft was stripped at launch,
  // so rebuild the paths from the Tracks the launch created — never
  // show a blank designer over a real design.
  const cycleTracksForReopen = useTrackStore(
    useShallow((s) => (cycleId ? s.tracks.filter((t) => t.cycleId === cycleId) : EMPTY_TRACKS)),
  );
  const reopenHydratedRef = useRef(false);
  const isReopen = searchParams.get('reopen') === '1';
  useEffect(() => {
    if (!isReopen || reopenHydratedRef.current) return;
    if (readDesignerDraft(cycle)) {
      reopenHydratedRef.current = true; // a live draft is newer than the launch
      return;
    }
    const rebuilt = pathsFromLaunchedTracks(cycleTracksForReopen);
    if (rebuilt.length === 0) return; // tracks still loading — retry on next change
    reopenHydratedRef.current = true;
    designer.setPaths(rebuilt);
    designer.setStep(2);
  }, [isReopen, cycle, cycleTracksForReopen, designer]);

  // ?step=N lets the create-cycle modal (which already collected setup) land
  // the Designer on Design Paths. The Cycle Setup pill stays clickable to go back.
  const requestedStep = searchParams.get('step');
  useEffect(() => {
    const n = Number(requestedStep);
    if (n >= 2 && n <= 4) designer.setStep(n as DesignerStep);
    // Initial-landing concern only — do not re-run when designer state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void fetchEnterpriseIdentity();
  }, [fetchEnterpriseIdentity]);

  useEffect(() => {
    if (cycleId) {
      void fetchTracksForCycle(cycleId);
      void fetchLoops(cycleId);
    }
  }, [cycleId, fetchTracksForCycle, fetchLoops]);

  // The whole-list load replaces the store. One that began before this cycle was
  // created takes it back out, and nothing else would bring it back: the page
  // spun for good right after "Create cycle".
  useEffect(() => {
    if (cycleId && !cycle) void fetchCycleById(cycleId);
  }, [cycleId, cycle, fetchCycleById]);

  if (!modulesReady) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48, background: 'var(--bg)', color: 'var(--text-ter)' }}>
        <CompassSpinner size={40} />
      </div>
    );
  }

  if (!designerEnabled) {
    return (
      <Navigate
        to={cycleId ? `/prioritization/cycles/${cycleId}/setup?step=1` : '/prioritization/cycles'}
        replace
      />
    );
  }

  if (!cycleId || !cycle) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48, background: 'var(--bg)', color: 'var(--text-ter)' }}>
        <CompassSpinner size={40} />
      </div>
    );
  }

  if (!canConfigure) {
    return <Navigate to={`/prioritization/cycles/${cycleId}`} replace />;
  }

  // W-2: ?reopen=1 lets configurators revise the design of a cycle whose first
  // request hasn't been sent (status still draft). Once the cycle leaves draft,
  // the one-way door is closed for real.
  // While launching, designerLaunchedAt is written early — do not bounce to
  // CycleDetail (the post-launch operating room) before we navigate to compose.
  const reopenRequested = searchParams.get('reopen') === '1' && cycle?.status === 'draft';
  if (isCycleDesignerComplete(cycle) && !reopenRequested && !launching) {
    return <Navigate to={`/prioritization/cycles/${cycleId}`} replace />;
  }

  // No org chart is not a dead end: a cycle can route through named people and
  // groups instead (canon 29 §2.1 — a Path Node is an org unit or a named
  // recipient). Offer that rather than stranding the designer.
  if (pathMode === 'org_chart' && !orgChartAvailable) {
    return (
      <div style={{ padding: 32, background: 'var(--bg)', color: 'var(--text-pri)', fontFamily: 'var(--sans)', maxWidth: 560 }}>
        <p style={{ marginBottom: 8 }}>
          This cycle has no org chart under its anchor to draw Catchball paths on.
        </p>
        <p style={{ color: 'var(--text-ter)', marginBottom: 20, lineHeight: 1.6 }}>
          You can build the ball path from named people and groups instead — pick
          them in the order the ball should travel — or set an org scope on the
          cycle first.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            type="button"
            onClick={() => designer.setPathMode('custom')}
            style={{
              padding: '8px 16px',
              background: 'var(--mustard)',
              border: 'none',
              color: INK_ON_ACCENT,
              fontFamily: 'var(--mono)',
              fontSize: '0.78rem',
              letterSpacing: '0.08em',
              cursor: 'pointer',
            }}
          >
            Build a custom hierarchy →
          </button>
          <Link to={`/prioritization/cycles/${cycleId}?advanced=1`} style={{ color: 'var(--mustard)' }}>
            Open detail view →
          </Link>
        </div>
      </div>
    );
  }

  const step = designer.step;

  const goBack = () => {
    if (step > 1) designer.setStep((step - 1) as DesignerStep);
  };

  const goNext = () => {
    if (step < 4) designer.setStep((step + 1) as DesignerStep);
  };

  const handleLaunch = async () => {
    setLaunching(true);
    setLaunchError(null);
    const orgById = designer.buildOrgById(effectiveOrgNodes);
    const launchUserId = useUserStore.getState().userId;
    const result = await launchCycleDesigner({
      cycle,
      form: designer.form,
      paths: designer.paths,
      orgNodes: effectiveOrgNodes,
      existingTracks: getTracksForCycle(cycle.id),
      existingLoops: loops,
      updateCycle,
      createTrack,
      archiveTrack,
      orgById,
      updateTrack,
      createLoop,
      updateLoopIntent,
      userId: launchUserId,
      pathMode,
      workspaceId,
      saveVirtualConfig: useCatchballVirtualConfigStore.getState().save,
      ownerRef: cycleOwnerRef ?? undefined,
    });
    if (!result.ok || !result.firstTrackId) {
      setLaunching(false);
      setLaunchError(result.error ?? 'Launch failed.');
      return;
    }
    // Keep launching=true through navigation so the designerLaunchedAt store
    // update cannot redirect through CycleDetail as an intermediate screen.
    navigate(buildDesignerLaunchUrl(cycle.id));
  };

  return (
    <div
      ref={rootRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)', // fallback until the measuring effect fits it exactly
        margin: '-22px -24px -24px',
        background: 'var(--bg)',
        color: 'var(--text-pri)',
        fontFamily: 'var(--sans)',
        overflow: 'hidden',
      }}
    >
      {/* Top nav */}
      {/* V-01 navigation contract: breadcrumb (never lose the shell), named stage,
          and a visible Detail-view toggle in place of the old hidden Advanced link. */}
      <nav style={{ height: 48, background: 'var(--bg-raised)', borderBottom: '1px solid var(--rule)', display: 'flex', alignItems: 'center', padding: '0 20px', flexShrink: 0 }}>
        <Link
          to="/prioritization/cycles"
          style={{ fontFamily: 'var(--mono)', fontSize: '0.7rem', letterSpacing: '0.18em', color: 'var(--text-ter)', textTransform: 'uppercase', textDecoration: 'none' }}
        >
          Cycles
        </Link>
        <span style={{ fontFamily: 'var(--mono)', fontSize: '0.7rem', color: 'var(--text-dim)', margin: '0 8px' }}>/</span>
        <span style={{ fontFamily: 'var(--serif)', fontSize: '1rem', fontWeight: 300, color: 'var(--text-sec)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {cycle?.name ?? 'Cycle Designer'}
        </span>
        <span
          style={{
            marginLeft: 12,
            padding: '2px 8px',
            border: '1px solid var(--mustard)',
            borderRadius: 999,
            fontFamily: 'var(--mono)',
            fontSize: '0.62rem',
            letterSpacing: '0.14em',
            color: 'var(--mustard)',
            textTransform: 'uppercase',
          }}
        >
          Design — not launched
        </span>
        <span style={{ flex: 1 }} />
        <Link
          to={`/prioritization/cycles/${cycleId}?advanced=1`}
          style={{
            fontFamily: 'var(--mono)',
            fontSize: '0.68rem',
            letterSpacing: '0.1em',
            color: 'var(--text-sec)',
            textDecoration: 'none',
            textTransform: 'uppercase',
            border: '1px solid var(--rule-bright)',
            borderRadius: 6,
            padding: '5px 10px',
          }}
          title="Open the full Cycle Detail page for this cycle (design stage continues until launch)"
        >
          Detail view →
        </Link>
      </nav>

      {/* Step pills */}
      <div style={{ height: 44, background: 'var(--bg-raised)', borderBottom: '1px solid var(--rule)', display: 'flex', alignItems: 'center', padding: '0 20px', flexShrink: 0 }}>
        {([1, 2, 3, 4] as DesignerStep[]).map((n, i) => (
          <div key={n} style={{ display: 'flex', alignItems: 'center' }}>
            {i > 0 && <span style={{ width: 24, height: 1, background: 'var(--rule-bright)' }} />}
            <button
              type="button"
              // Steps are clickable in BOTH directions — mirrors the footer
              // buttons for people who look at the top instead of bottom-right.
              onClick={() => designer.setStep(n)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '0 16px',
                height: 44,
                background: 'none',
                border: 'none',
                borderBottom: step === n ? '2px solid var(--mustard)' : '2px solid transparent',
                color: step === n ? 'var(--mustard)' : n < step ? 'var(--text-ter)' : 'var(--text-dim)',
                fontFamily: 'var(--mono)',
                fontSize: '0.72rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  background: n < step ? 'var(--green)' : step === n ? 'var(--mustard)' : 'var(--rule-bright)',
                  color: n <= step ? INK_ON_ACCENT : 'var(--text-dim)',
                }}
              >
                {n < step ? '✓' : n}
              </span>
              {STEP_LABELS[n]}
            </button>
          </div>
        ))}
      </div>

      {/* Main layout */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <DesignerLeftRail
          paths={designer.paths}
          activePathId={designer.activePathId}
          anchor={effectiveAnchor}
          onSelectPath={designer.setActivePathId}
          onRenamePath={designer.renamePath}
          onRemovePath={designer.removePath}
          onAddPath={() => designer.addPath(effectiveAnchorId)}
        />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          {step === 1 && <StepSetup form={designer.form} onPatch={designer.patchForm} />}
          {step === 2 && (
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 280px', minHeight: 0, overflow: 'hidden' }}>
              <StepDesignPaths
                form={designer.form}
                state={designer}
                orgNodes={effectiveOrgNodes}
                anchor={effectiveAnchor}
                maxRelativeLevel={effectiveMaxRelativeLevel}
                anchorId={effectiveAnchorId}
                customAnchorLabel={customAnchorLabel}
                orgChartAvailable={orgChartAvailable}
                cycleOwner={cycleOwnerRef}
              />
            </div>
          )}
          {step === 3 && (
            <StepParameters form={designer.form} paths={designer.paths} orgNodes={effectiveOrgNodes} onUpdateParam={designer.updateParam} onSetNodeWindow={designer.setNodeWindow} />
          )}
          {step === 4 && (
            <StepReviewLaunch
              form={designer.form}
              paths={designer.paths}
              orgNodes={effectiveOrgNodes}
              maxRelativeLevel={effectiveMaxRelativeLevel}
              launching={launching}
              launchError={launchError}
              onLaunch={handleLaunch}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <footer style={{ height: 52, borderTop: '1px solid var(--rule)', background: 'var(--bg-raised)', display: 'flex', alignItems: 'center', padding: '0 20px', gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-dim)', flex: 1 }}>{STEP_HINTS[step]}</span>
        {step > 1 && step < 4 && (
          <button type="button" onClick={goBack} style={footerBtnStyle(false)}>
            ← Back
          </button>
        )}
        {step === 4 ? (
          <button type="button" onClick={goBack} style={footerBtnStyle(false)}>← Guidance</button>
        ) : (
          <button type="button" onClick={goNext} style={footerBtnStyle(true)}>
            {step === 1 ? 'Design Paths →' : step === 2 ? 'Add Guidance →' : 'Review & Launch →'}
          </button>
        )}
      </footer>

      {designer.toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--surface)',
            border: '1px solid var(--green)',
            padding: '9px 16px',
            fontSize: '0.95rem',
            zIndex: 1000,
          }}
        >
          {designer.toast}
        </div>
      )}
    </div>
  );
}

function footerBtnStyle(primary: boolean): React.CSSProperties {
  return {
    padding: '8px 16px',
    background: primary ? 'var(--slate)' : 'transparent',
    border: primary ? 'none' : '1px solid var(--rule-bright)',
    color: primary ? '#fff' : 'var(--text-ter)',
    fontFamily: 'var(--mono)',
    fontSize: '0.78rem',
    letterSpacing: '0.08em',
    cursor: 'pointer',
  };
}

export default CycleDesignerWizard;
